/**
 * @voltxampere/components — Bipolar junction transistor (NPN + PNP)
 *
 * Sprint 7 promotes the Sprint 4 placeholder `bjt` type into two
 * canonical flavours with full Ebers-Moll parameter sets:
 *
 *   - `bjt-npn`: conventional collector-up biasing (Ic flows IN to
 *     the collector, Ib flows IN to the base).
 *   - `bjt-pnp`: complementary device, sign-flipped currents.
 *
 * The two share the same 3-pin layout (base / collector / emitter)
 * and the same Zod parameter schema. The polarity is encoded in the
 * component **type** (a single registry lookup distinguishes them)
 * rather than a polarity param, which makes the engine's stamp
 * dispatch O(1) without extra branching.
 *
 * Parameter set mirrors SPICE / Gray & Meyer (*Analysis and Design
 * of Analog Integrated Circuits*, Ch. 1) notation:
 *
 *   - Is   : transport saturation current (A)
 *   - Bf   : forward common-emitter current gain (β_F)
 *   - Br   : reverse common-emitter current gain (β_R)
 *   - Vaf  : forward Early voltage (V) — slope of Ic(Vce)
 *   - Var  : reverse Early voltage (V)
 *   - Nf   : forward (BE) emission coefficient (ideality, typically 1)
 *   - Nr   : reverse (BC) emission coefficient (typically 1)
 *
 * Sprint 5 shipped a schema with only (polarity, forwardBeta,
 * reverseBeta, saturationCurrent). Sprint 7's schema was a strict
 * superset — the Schema migration (0.1.0 → 0.2.0) maps legacy
 * `bjt` instances to `bjt-npn` (default NPN assumption) and fills
 * in the remaining defaults. Sprint 8c renames the BE/BC emission
 * coefficients (`Ne`/`Nc`) to ngspice-aligned names (`Nf`/`Nr`)
 * AND flips the reverse default from `2` to `1`: the Sprint 8b.1
 * diagnostic (see `docs/sprints/diagnostics/sprint-8c-pnp-diagnostic.md`
 * Test 1) traced a 6% residual at the PNP CE operating point to
 * the over-damped reverse term. The rename is carried by the
 * 0.2.0 → 0.3.0 migration; circuits saved with an explicit old
 * name have values preserved.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { z } from "zod";
/**
 * Shared Ebers-Moll parameter set. Both polarities use this schema
 * — the sign flipping happens inside the engine stamp, not at the
 * schema layer.
 */
export const BjtParamsSchema = z
    .object({
    /** Transport saturation current (A). Default 1e-16 A — generic
     *  small-signal transistor. */
    Is: z.number().positive().finite().default(1e-16),
    /** Forward β (I_C / I_B in active region). Default 100. */
    Bf: z.number().positive().finite().default(100),
    /** Reverse β (I_E / I_B in reverse-active). Default 1. */
    Br: z.number().positive().finite().default(1),
    /** Forward Early voltage (V). Default 100 V — moderate output
     *  resistance. Use `Infinity` to disable the Early effect. */
    Vaf: z.number().positive().finite().default(100),
    /** Reverse Early voltage (V). Default 100 V. */
    Var: z.number().positive().finite().default(100),
    /** Forward (BE) emission coefficient. Default 1 (ideal). ngspice
     *  `.model ... NPN (NF=1 …)` convention. Sprint 8c renamed from
     *  `Ne`; see the 0.2.0 → 0.3.0 migration. */
    Nf: z.number().positive().finite().default(1),
    /** Reverse (BC) emission coefficient. Default 1 (ngspice-aligned).
     *  Sprint 8c flipped the default from 2 to 1 after the Sprint 8b.1
     *  PNP-CE diagnostic traced a knee-asymmetry residual of ~6% to
     *  the over-damped reverse term. Circuits that explicitly saved
     *  `Nc: 2` keep their value through the 0.2.0 → 0.3.0 migration
     *  (value-preservation). */
    Nr: z.number().positive().finite().default(1),
    /**
     * Sprint 22 D3 / D-22-6 — junction capacitance + transit-time
     * parameters (zero-default, additive — Sprint 5/6/7/20 fixtures
     * stay exactly zero-diff).
     *
     *   Cje:  zero-bias B-E junction capacitance      (F)
     *   Cjc:  zero-bias B-C junction capacitance      (F)
     *   Tf:   forward transit time                    (s)
     *   Vje:  built-in B-E junction voltage           (V)   default 0.7
     *   Mje:  B-E junction grading coefficient        (—)   default 0.33
     *   Vjc:  built-in B-C junction voltage           (V)   default 0.7
     *   Mjc:  B-C junction grading coefficient        (—)   default 0.33
     *   FC:   SPICE forward-bias linearisation knee   (—)   default 0.5
     *
     * Hybrid-π AC capacitance contributions:
     *   Cπ(Vbe) = Cje·(1 - Vbe/Vje)^-Mje + gm·Tf
     *   Cμ(Vbc) = Cjc·(1 - Vbc/Vjc)^-Mjc
     */
    Cje: z.number().nonnegative().finite().optional(),
    Cjc: z.number().nonnegative().finite().optional(),
    Tf: z.number().nonnegative().finite().optional(),
    Vje: z.number().positive().finite().optional(),
    Mje: z.number().positive().finite().optional(),
    Vjc: z.number().positive().finite().optional(),
    Mjc: z.number().positive().finite().optional(),
    FC: z.number().min(0).lt(1).optional(),
    /**
     * Sprint 22 D3 / D-22-5 — parazitik series resistances. Sprint
     * 24 B1 wires them through the full SPICE3 bjtload pattern: a
     * non-zero value allocates an internal node (B'/C'/E') between
     * the external pin and the Ebers-Moll junction. Zero-default
     * stays bit-identical to Sprint 7 (sentinels intact).
     *
     *   Re:  emitter parazitik resistance (Ω)  — default 0
     *   Rb:  base parazitik resistance     (Ω)  — default 0
     *   Rc:  collector parazitik resistance (Ω) — default 0
     */
    Re: z.number().nonnegative().finite().optional(),
    Rb: z.number().nonnegative().finite().optional(),
    Rc: z.number().nonnegative().finite().optional(),
})
    .strict();
/**
 * Canonical BJT pin layout — base / collector / emitter.
 *
 * Sprint 27 Lane 6 C2 (S27-LANE17-FOLLOWUP) — declared here so the
 * Sprint 26 Lane 17 sentinel report §4.1 topology table
 * (`Q1 | bjt-pnp | base=net_b, collector=net_c, emitter=net_e`) has
 * an addressable schema citation. Both `bjt-npn` and `bjt-pnp`
 * reuse this constant — the 3-pin layout is polarity-invariant
 * (the engine stamp flips internal voltage signs, not the pin
 * positions). Geometric layout (left / top / bottom) drives the
 * `<vxa-canvas>` symbol renderer; the MNA stamp consults pin `id`
 * only.
 */
const bjtPins = [
    { id: "base", side: "left", offset: 0, label: "B" },
    { id: "collector", side: "top", offset: 0, label: "C" },
    { id: "emitter", side: "bottom", offset: 0, label: "E" },
];
/**
 * NPN BJT. Canonical device for the Sprint 7 analytical tests —
 * the common-emitter amplifier at §9 of the sprint prompt is NPN.
 */
export const bjtNpnDefinition = {
    type: "bjt-npn",
    category: "active",
    displayName: { tr: "Transistör (NPN)", en: "Transistor (NPN)" },
    symbol: "Q",
    pins: bjtPins,
    parameterSchema: BjtParamsSchema,
    nonlinear: true,
};
/**
 * PNP BJT. Same layout as NPN; the engine stamp flips the sign of
 * every internal voltage and current so upstream consumers can
 * treat both the same at schematic level.
 */
export const bjtPnpDefinition = {
    type: "bjt-pnp",
    category: "active",
    displayName: { tr: "Transistör (PNP)", en: "Transistor (PNP)" },
    symbol: "Q",
    pins: bjtPins,
    parameterSchema: BjtParamsSchema,
    nonlinear: true,
};
/**
 * Legacy alias kept for Sprint 6 fixtures that still type-check.
 * The migration at `@voltxampere/schema` rewrites `type: "bjt"` to
 * `type: "bjt-npn"` at load time, so this placeholder only exists
 * for the transition window. The engine stamp registry does NOT
 * register the bare `bjt` type — unregistered types throw a clear
 * error. Consumers authoring new circuits should spell `bjt-npn`
 * or `bjt-pnp` explicitly.
 */
export const bjtDefinition = bjtNpnDefinition;
//# sourceMappingURL=bjt.js.map